import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth, EXTERNAL_USER_ROLE_IDS } from '@features/auth/hooks/useAuth';
import { findCurrentUserRoleData } from '@features/auth/utils/findCurrentUserRoleData.ts';
import { Permission } from '@shared/consts/permissions';
import { useRolesDetailsContext } from '@shared/providers/RolesDetailsProvider/useRolesDetailsContext';
import { logError } from '@shared/utils/logError.ts';
import { navigateTo } from '@shared/utils/navigateTo.tsx';

interface UseControlAccessParams {
  requiredPermissions?: Permission[];
  accessDeniedRedirectTo?: string;
  additionalCondition?: boolean;
}

interface UseControlAccessReturn {
  isUserAllowedToUseResource: boolean;
}

const hasRequiredPermissions = (userPermissions: Permission[], requiredPermissions: Permission[]) => {
  return requiredPermissions.some((requiredPermission) => userPermissions.includes(requiredPermission));
};

export const useControlAccess = ({
  requiredPermissions,
  additionalCondition,
  accessDeniedRedirectTo,
}: UseControlAccessParams): UseControlAccessReturn => {
  const [pass, setPass] = useState<boolean>(false);
  const navigate = useNavigate();
  const { getRoleId } = useAuth();
  const rolesDetailsContext = useRolesDetailsContext();

  useEffect(() => {
    (async () => {
      if (!requiredPermissions || requiredPermissions.length === 0) {
        setPass(true);
        return;
      }

      const userRoleId = await getRoleId();
      const isExternalDirectClient = userRoleId === EXTERNAL_USER_ROLE_IDS.DIRECT_CLIENT_ID;
      const isExternalAgency = userRoleId === EXTERNAL_USER_ROLE_IDS.AGENCY_ID;

      if (!userRoleId) {
        return;
      }

      const userPermissions = findCurrentUserRoleData({
        rolesDetails: rolesDetailsContext,
        userRoleId,
        isExternalDirectClient,
        isExternalAgency,
      })?.permissions;

      if (!userPermissions || !userPermissions.length) {
        return;
      }

      const conditionPassed = hasRequiredPermissions(userPermissions, requiredPermissions);
      setPass(conditionPassed);

      if (!conditionPassed && accessDeniedRedirectTo) {
        void navigateTo(navigate, accessDeniedRedirectTo).catch(logError);
      }
    })();
  }, [getRoleId, rolesDetailsContext]);

  const additionalConditionPassed = additionalCondition === undefined || additionalCondition;
  const isUserAllowedToUseResource = pass && additionalConditionPassed;

  return {
    isUserAllowedToUseResource,
  };
};
